import { useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { StatusBadge } from "./StatusBadge";
import { ImageIcon, User, Loader2, RefreshCw, Sparkles, Replace, X } from "lucide-react";

export function AgentCompositeSection({ agentComposite, images, generating, onGenerate, onGenerateWithBg }) {
  const [bgPickerOpen, setBgPickerOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const compositeReady = agentComposite.status === 'ready';

  return (
    <>
      <section className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden" data-testid="production-agent-composite">
        <div className="px-5 py-4 border-b border-[#E2E4E7] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
              <ImageIcon size={18} className="text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>Agent Composite</h3>
              <p className="text-xs text-[#94969C]">Agent placed in property background — used for both Intro and CTA</p>
            </div>
          </div>
          <StatusBadge status={agentComposite.status} />
        </div>
        <div className="p-5">
          <div className="flex gap-5">
            <div className="w-64 shrink-0">
              <div className={`aspect-video rounded-lg bg-[#F3F3F9] overflow-hidden border border-[#E2E4E7] relative ${agentComposite.image_url ? 'cursor-pointer group' : ''}`}
                onClick={() => agentComposite.image_url && setLightboxOpen(true)}
                data-testid="composite-preview">
                {agentComposite.image_url ? (
                  <>
                    <img src={agentComposite.image_url} alt="Agent composite" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ImageIcon size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                    <User size={24} className="text-[#D8D9DD]" />
                    <span className="text-[10px] text-[#94969C]">Not generated yet</span>
                  </div>
                )}
                {agentComposite.status === 'generating' && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 size={24} className="text-white animate-spin" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0 space-y-3">
              {agentComposite.progress && agentComposite.status === 'generating' && (
                <p className="text-xs text-amber-600 animate-pulse">{agentComposite.progress}</p>
              )}
              {agentComposite.background_reasoning && agentComposite.status === 'ready' && (
                <p className="text-xs text-emerald-600">
                  Background: Image #{agentComposite.background_index} — {agentComposite.background_reasoning}
                </p>
              )}
              {agentComposite.error && agentComposite.status === 'failed' && (
                <p className="text-xs text-red-600">{agentComposite.error}</p>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={() => onGenerate(-1)}
                  disabled={generating}
                  data-testid="gen-agent-composite-btn"
                  className="text-xs bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-7">
                  {generating ? <><Loader2 size={11} className="mr-1 animate-spin" /> Generating...</> :
                   agentComposite.status === 'failed' ? <><RefreshCw size={11} className="mr-1" /> Retry (AI picks bg)</> :
                   compositeReady ? <><RefreshCw size={11} className="mr-1" /> Regenerate (AI picks bg)</> :
                   <><Sparkles size={11} className="mr-1" /> Generate (AI picks bg)</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBgPickerOpen(true)}
                  disabled={generating}
                  data-testid="pick-bg-btn"
                  className="text-xs h-7 border-[#E2E4E7]">
                  <Replace size={11} className="mr-1" /> Choose background
                </Button>
              </div>
              {bgPickerOpen && (
                <div className="border border-[#E2E4E7] rounded-lg p-3 space-y-2" data-testid="bg-picker">
                  <p className="text-xs font-medium text-[#020B23]">Select a property photo as background:</p>
                  <div className="grid grid-cols-5 gap-2 max-h-[150px] overflow-y-auto">
                    {images.map((url, idx) => (
                      <button key={idx} onClick={() => { onGenerateWithBg(idx); setBgPickerOpen(false); }}
                        data-testid={`bg-pick-${idx}`}
                        className={`aspect-video rounded overflow-hidden border-2 transition-all ${agentComposite.background_index === idx ? 'border-[#4D11F6] ring-2 ring-[#4D11F6]/20' : 'border-[#E2E4E7] hover:border-[#4D11F6]/40'}`}>
                        <img src={url} alt={`Photo ${idx}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setBgPickerOpen(false)} className="text-xs h-6">Cancel</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="sm:max-w-3xl bg-black/95 border-none p-0" data-testid="composite-lightbox">
          <button onClick={() => setLightboxOpen(false)}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <X size={16} className="text-white" />
          </button>
          {agentComposite.image_url && (
            <img src={agentComposite.image_url} alt="Agent composite full size"
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
